import { useCallback, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  readSprintPlanDefaultBoardId,
  shouldSyncSprintPlanStorageKey,
  SPRINT_PLAN_BOARD_CHANGED_EVENT,
} from '../../../shared/constants/sprintPlanBoard';
import { PeriodOptions, SprintOption, SprintPickerMeta } from './types';
import { useApiQuery, useQueryClient } from '../../../shared/hooks/useApiQuery';

const EMPTY: PeriodOptions = { sprints: [], releases: [], quarters: [] };

function mapJiraSprintRow(s: Record<string, unknown>): SprintOption {
  const idRaw = s.id;
  const id = idRaw != null ? String(idRaw) : '';
  const name = typeof s.name === 'string' ? s.name : '';
  const stateRaw = s.state;
  const state =
    stateRaw === 'active' || stateRaw === 'closed' || stateRaw === 'future' ? stateRaw : undefined;
  return {
    id,
    name,
    startDate: typeof s.startDate === 'string' ? s.startDate : undefined,
    endDate: typeof s.endDate === 'string' ? s.endDate : undefined,
    state,
  };
}

export function usePeriodOptions(productId: string | undefined): {
  options: PeriodOptions;
  loading: boolean;
  error: string | null;
  reload: () => void;
  sprintPickerMeta: SprintPickerMeta;
} {
  const queryClient = useQueryClient();
  const { currentOrg } = useAuth();
  const isDemo = Boolean(currentOrg?.is_demo);

  const savedBoardId = productId ? readSprintPlanDefaultBoardId(productId) : null;
  const hasSaved = Boolean(savedBoardId && savedBoardId.trim() !== '');

  const periodQuery = useApiQuery<{
    releases: PeriodOptions['releases'];
    quarters: PeriodOptions['quarters'];
  }>(['analytics', 'period-options', productId], {
    path: '/api/analytics/period-options',
    enabled: !!productId,
    staleTime: 60_000,
  });

  const demoBoardsQuery = useApiQuery<{ boards?: { id: number }[] }>(
    ['analytics', 'demo-boards', productId],
    {
      path: '/api/jira/boards',
      enabled: !!productId && isDemo && !hasSaved,
      staleTime: 60_000,
    }
  );

  const demoBoardId =
    isDemo && !hasSaved && demoBoardsQuery.data?.boards?.length
      ? String(demoBoardsQuery.data.boards[0]!.id)
      : null;
  const boardId = hasSaved ? savedBoardId : demoBoardId;
  const hasBoard = Boolean(boardId && boardId.trim() !== '');

  const sprintsQuery = useApiQuery<{ sprints?: unknown[] }>(
    ['analytics', 'board-sprints', productId, boardId],
    {
      path: `/api/jira/boards/${encodeURIComponent(boardId ?? '')}/sprints?startAt=0&maxResults=500&order=analytics`,
      enabled: !!productId && hasBoard,
      staleTime: 60_000,
    }
  );

  const options: PeriodOptions = useMemo(() => {
    if (!productId) return EMPTY;

    const releases = periodQuery.data?.releases ?? [];
    const quarters = periodQuery.data?.quarters ?? [];

    let sprints: SprintOption[] = [];
    if (hasBoard && sprintsQuery.data) {
      const raw = Array.isArray(sprintsQuery.data.sprints) ? sprintsQuery.data.sprints : [];
      sprints = raw
        .map((row) => mapJiraSprintRow(row as Record<string, unknown>))
        .filter((x) => x.id && x.name);
    }

    return { sprints, releases, quarters };
  }, [productId, periodQuery.data, sprintsQuery.data, hasBoard]);

  const loading =
    (!productId ? false : periodQuery.isLoading) ||
    (isDemo && !hasSaved && demoBoardsQuery.isLoading) ||
    (hasBoard && sprintsQuery.isLoading);

  const error =
    periodQuery.error?.message ??
    demoBoardsQuery.error?.message ??
    sprintsQuery.error?.message ??
    null;

  const listError = sprintsQuery.error?.message ?? null;
  const sprintPickerMeta: SprintPickerMeta = useMemo(
    () => ({ hasSavedDefaultBoard: hasSaved || isDemo, listError }),
    [hasSaved, isDemo, listError]
  );

  const reload = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['analytics', 'period-options', productId] });
    queryClient.invalidateQueries({ queryKey: ['analytics', 'demo-boards', productId] });
    queryClient.invalidateQueries({ queryKey: ['analytics', 'board-sprints', productId, boardId] });
  }, [queryClient, productId, boardId]);

  useEffect(() => {
    if (!productId) return;

    const onStorage = (e: StorageEvent) => {
      if (shouldSyncSprintPlanStorageKey(e.key, productId)) {
        queryClient.invalidateQueries({ queryKey: ['analytics', 'board-sprints', productId] });
        queryClient.invalidateQueries({ queryKey: ['analytics', 'demo-boards', productId] });
      }
    };
    const onBoardEvent = (ev: Event) => {
      const d = (ev as CustomEvent<{ productId?: string }>).detail;
      if (d?.productId != null && d.productId !== productId) return;
      queryClient.invalidateQueries({ queryKey: ['analytics', 'board-sprints', productId] });
      queryClient.invalidateQueries({ queryKey: ['analytics', 'demo-boards', productId] });
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(SPRINT_PLAN_BOARD_CHANGED_EVENT, onBoardEvent);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(SPRINT_PLAN_BOARD_CHANGED_EVENT, onBoardEvent);
    };
  }, [productId, queryClient]);

  return { options, loading, error, reload, sprintPickerMeta };
}
